import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

const useMocks = String(import.meta.env.VITE_USE_MOCKS || 'false') === 'true';
const MICROSOFT_STATE_KEY = 'nudge.microsoft.oauth.v1';

function activeWorkspaceId(fallback = '') {
  return String(fallback || localStorage.getItem('nudge.workspace') || '').trim();
}

function activeOrganizationId() {
  return String(localStorage.getItem('nudge.organization') || '').trim();
}


let googleScriptPromise;

function callable(name) {
  if (!functions) throw new Error('Firebase Functions is not configured.');
  return httpsCallable(functions, name);
}

function loadGoogleIdentityServices() {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (googleScriptPromise) return googleScriptPromise;

  googleScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existing) {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Could not load Google authorization.'));
    document.head.appendChild(script);
  });

  return googleScriptPromise;
}

async function connectGoogle({ workspaceId = '', loginHint = '' } = {}) {
  const clientId = import.meta.env.VITE_REACT_APP_GOOGLE_CLIENT_ID;
  if (!clientId) throw new Error('VITE_REACT_APP_GOOGLE_CLIENT_ID is missing.');

  await loadGoogleIdentityServices();

  const response = await new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: [
        'openid',
        'email',
        'profile',
        'https://www.googleapis.com/auth/gmail.modify',
      ].join(' '),
      ux_mode: 'popup',
      prompt: 'consent',
      select_account: !loginHint,
      ...(loginHint ? { login_hint: loginHint } : {}),
      include_granted_scopes: true,
      callback: (result) => {
        if (result?.error) {
          reject(new Error(result.error_description || result.error));
          return;
        }
        if (!result?.code) {
          reject(new Error('Google did not return an authorization code.'));
          return;
        }
        resolve(result);
      },
      error_callback: (error) => {
        reject(new Error(error?.message || error?.type || 'Google authorization was interrupted.'));
      },
    });

    client.requestCode();
  });

  const result = await callable('nudgeGoogleAuth')({
    authCode: response.code,
    redirectUri: window.location.origin,
    workspaceId: activeWorkspaceId(workspaceId),
    organizationId: activeOrganizationId(),
  });
  if (!result.data?.ok) throw new Error(result.data?.error || 'Could not connect Gmail.');
  return result.data;
}

function microsoftRedirectUri() {
  return (
    import.meta.env.VITE_REACT_APP_MICROSOFT_NUDGE_REDIRECT_URI ||
    `${window.location.origin}/oauth/microsoft/callback`
  );
}

function connectMicrosoft({ workspaceId = '' } = {}) {
  const clientId = import.meta.env.VITE_REACT_APP_MICROSOFT_CLIENT_ID;
  if (!clientId) throw new Error('VITE_REACT_APP_MICROSOFT_CLIENT_ID is missing.');

  const redirectUri = microsoftRedirectUri();
  const state = crypto.randomUUID();
  const session = {
    state,
    redirectUri,
    returnTo: `${window.location.pathname}${window.location.search || ''}`,
    createdAt: Date.now(),
    workspaceId: activeWorkspaceId(workspaceId),
    organizationId: activeOrganizationId(),
  };
  sessionStorage.setItem(MICROSOFT_STATE_KEY, JSON.stringify(session));

  const scopes = [
    'Mail.ReadWrite',
    'Mail.Send',
    'MailboxSettings.ReadWrite',
    'User.Read',
    'offline_access',
    'openid',
    'profile',
  ].join(' ');

  const url = new URL('https://login.microsoftonline.com/common/oauth2/v2.0/authorize');
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('response_mode', 'query');
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('scope', scopes);
  url.searchParams.set('prompt', 'select_account');
  url.searchParams.set('state', state);

  window.location.assign(url.toString());
  return { redirecting: true };
}

export function getMicrosoftOAuthSession() {
  try {
    return JSON.parse(sessionStorage.getItem(MICROSOFT_STATE_KEY) || 'null');
  } catch {
    return null;
  }
}

export function clearMicrosoftOAuthSession() {
  sessionStorage.removeItem(MICROSOFT_STATE_KEY);
}

export async function completeMicrosoftMailboxConnection({ code, state }) {
  const session = getMicrosoftOAuthSession();
  if (!session?.state || session.state !== state) {
    throw new Error('The Microsoft connection session is missing or expired. Please try connecting again.');
  }
  if (Date.now() - Number(session.createdAt || 0) > 15 * 60 * 1000) {
    clearMicrosoftOAuthSession();
    throw new Error('The Microsoft connection session expired. Please try again.');
  }

  const result = await callable('nudgeOutlookAuthCallback')({
    code,
    redirectUri: session.redirectUri,
    workspaceId: session.workspaceId || activeWorkspaceId(),
    organizationId: session.organizationId || activeOrganizationId(),
  });
  if (!result.data?.ok) throw new Error(result.data?.error || 'Could not connect Outlook.');
  return { ...result.data, returnTo: session.returnTo || '/setup' };
}

export async function testImapConnection(settings) {
  const result = await callable('nudgeTestImapConnection')({ settings, workspaceId: activeWorkspaceId(), organizationId: activeOrganizationId() });
  return result.data;
}

export async function saveImapConnection(provider, settings, workspaceId = '') {
  const result = await callable('nudgeSaveImapConnection')({ provider, settings, workspaceId: activeWorkspaceId(workspaceId), organizationId: activeOrganizationId() });
  if (!result.data?.ok) throw new Error(result.data?.error || 'Could not save this mailbox.');
  return result.data;
}

export async function connectMailbox(provider, userEmail, options = {}) {
  if (useMocks) {
    await new Promise((resolve) => setTimeout(resolve, 650));
    const domains = {
      google: 'gmail.com',
      microsoft: 'outlook.com',
      yahoo: 'yahoo.com',
      icloud: 'icloud.com',
      imap: 'example.com',
    };
    return {
      id: `mock-${provider}-${Date.now()}`,
      userId: 'mock-user',
      email: userEmail || `you@${domains[provider] || 'example.com'}`,
      provider,
      connected: true,
      health: 'healthy',
    };
  }

  if (provider === 'google') return connectGoogle({ workspaceId: options.workspaceId, loginHint: options.loginHint || '' });
  if (provider === 'microsoft') return connectMicrosoft({ workspaceId: options.workspaceId });

  throw new Error('Open the IMAP setup form to connect this provider.');
}

